import { useSelector } from "react-redux";
import ICONS from "../../../assets/Icons";
import SvgComponent from "../SVGComponent";

/**
 * CommonMoreActionsCard
 *
 * Reusable card component for rendering "More Actions" menu items.
 *
 * @param {Object} props
 * @param {string} props.title - Card header title.
 * @param {Array} props.actions - List of action items.
 * @param {string} [props.type] - Optional card type. Supported values:
 *                               "privateTxn" | "taxTxn".
 *
 * Action Object:
 * {
 *   label: string,      // Display label
 *   service: string,    // Service code used for access validation
 *   onClick: Function   // Click handler
 * }
 *
 * Example:
 *
 * <CommonMoreActionsCard
 *   title="More actions"
 *   actions={[
 *     {
 *       label: "MIS report",
 *       service: "SMisUser",
 *       onClick: () =>
 *         navigateWithServiceCheck({
 *           service: "SMisUser",
 *           route: "/tax_transaction_dashboard/generate_mis_report",
 *         }),
 *     },
 *     {
 *       label: "Upload file view all",
 *       service: "VTransactionUpload",
 *       onClick: () =>
 *         navigateWithServiceCheck({
 *           service: "VTransactionUpload",
 *           route:
 *             "/tax_transaction_dashboard/upload_transactions_view_all",
 *         }),
 *     },
 *   ]}
 * />
 *
 * Notes:
 * - If `service` is provided and user does not have access,
 *   the action is automatically shown with disabled styling.
 * - Click handling remains controlled by the parent component.
 */

const CommonMoreActionsCard = ({ title, actions = [], type }) => {
  const services = useSelector((state) => state.refreshToken.services);

  return (
    <div className={`flex flex-col w-full  Qlg:right-right-base`}>
      <div className="w-full rounded-border-radius-md bg-primary-card flex flex-col">
        <div className="w-full h-full">
          <div className="w-full px-padding-md py-padding-sm flex items-center gap-gap-sm">
            <img
              src={ICONS?.ADD}
              alt="add action"
              className="w-icon-sm h-icon-sm"
            />
            <p className="m-margin-base text-font-sm font-semibold text-text-primary">
              {title}
            </p>
          </div>
          <div className="border-b border-primary-bg mx-margin-md" />
          <div className="p-padding-sm flex flex-col gap-gap-sm">
            {actions.map(({ label, onClick, service }, index) => {
              const hasAccess = !service || (services || []).includes(service);
              return (
                <div
                  key={label}
                  className="flex flex-col w-full"
                >
                  <div
                    className={`flex w-full flex-row justify-between items-center rounded-border-radius-md relative z-z-index-base ${
                      hasAccess
                        ? "cursor-pointer global-hover-white"
                        : "cursor-not-allowed opacity-light-opacity"
                    }`}
                    onClick={onClick}
                  >
                    <div className="flex items-center gap-gap-xs p-padding-sm">
                      <p className="w-auto text-left text-font-xs text-text-primary opacity-base-opacity m-margin-base">
                        {label}
                      </p>
                    </div>

                    <div className="flex justify-center items-center mr-margin-sm">
                      <SvgComponent
                        url={ICONS?.ARROW_RIGHT_WHITE}
                        height="var(--icon-sm)"
                        width="var(--icon-sm)"
                        color="var(--text-primary)"
                      />
                    </div>
                  </div>

                  {index !== actions.length - 1 && (
                    <div className="border-b border-primary-bg mx-margin-sm mt-margin-sm" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonMoreActionsCard;
